import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { cn } from '@muzly/lib/utils';
import { FALLBACK_LANGUAGE } from '@muzly/shared/i18n';
import { ChevronDown } from 'lucide-react';

import { toLanguage } from '@muzly/shared';

import {
  POLICY_LANGUAGES,
  formatEffective,
  pickPolicyLanguage,
  policyVersions,
  resolvePolicy,
} from './policies';
import { PolicyDocument } from './PolicyDocument';
import { PageHeader } from '../../components';
import { htmlLang } from '../../core';

import type { PolicyKind, PolicyRegistry } from './policies';

/**
 * 이용약관·개인정보처리방침(Figma `MY #이용약관`·`MY #개인정보처리방침`) — 시행일을 골라
 * 그 버전을 읽는다.
 *
 * **버전은 주소에 있다**(`?v=YYYY-MM-DD`). 알약으로 고르면 주소가 바뀌고, 그 주소를 받은
 * 사람은 같은 버전을 본다. `v` 가 없거나 모르는 값이면 최신이다 — 낡은 링크가 빈 화면이
 * 되지 않는다(`resolvePolicy`). 고르는 것은 이동이 아니라 같은 화면의 상태라 히스토리를
 * 쌓지 않는다(`replace`) — 뒤로가기는 한 번에 MY 로 간다.
 *
 * **언어.** 앱 안 화면(`/more/docs/*`)은 화면 언어를 따른다. 앱 밖 화면(`standalone`:
 * `/policy/terms`·`/policy/privacy` — 네이티브의 인앱 웹뷰 모달과 브라우저 새 탭)은
 * `?lang=` → 화면 언어 → 한국어 순이다. 거기서는 앱이 고른 언어를 못 볼 수 있어 여는 쪽이
 * 주소에 실어 준다(`MorePage`). 화면의 글자(제목·알약)도 문서와 같은 언어로 적는다 — 영어
 * 방침 위에 한국어 제목이 서지 않는다.
 *
 * 앱 밖 화면에는 돌아갈 앱 스택이 없어(모달·새 탭) 헤더 대신 문서 제목을 `h1` 로 두고,
 * 언어 전환 링크를 같이 둔다. 탭 셸 밖이라 `main` 랜드마크도 이 화면이 직접 세우고, 탭
 * 제목과 문서의 `lang` 을 그 문서에 맞춘다 — 영어 방침의 탭이 한국어 앱 제목으로 뜨지 않는다.
 */
export const PolicyPage = ({
  kind,
  standalone = false,
  registry,
}: {
  kind: PolicyKind;
  /** 앱 스택 밖 화면 — 뒤로가기 헤더 없이 본문 위에 제목을 둔다. */
  standalone?: boolean;
  /** 테스트 주입점 — 버전이 여럿인 목록을 넣어 본다. 기본은 실제 목록. */
  registry?: PolicyRegistry;
}) => {
  const { i18n } = useTranslation();
  const [params, setParams] = useSearchParams();

  const lang = pickPolicyLanguage(
    standalone ? params.get('lang') : null,
    i18n.resolvedLanguage ?? i18n.language,
  );
  const t = i18n.getFixedT(lang);

  const version = resolvePolicy(kind, params.get('v'), registry);
  const title = version?.docs[lang].title;

  useEffect(() => {
    if (!standalone || !title) {
      return;
    }
    const root = document.documentElement;
    const previousTitle = document.title;
    document.title = title;
    root.lang = lang;
    return () => {
      document.title = previousTitle;
      // 찍어 둔 값이 아니라 지금의 화면 언어로 되돌린다 — 그 사이 언어가 바뀌었을 수 있다.
      root.lang = htmlLang(
        toLanguage(i18n.resolvedLanguage ?? i18n.language) ?? FALLBACK_LANGUAGE,
      );
    };
  }, [standalone, title, lang, i18n]);

  // 목록이 빈 종류는 없다(`policies.spec`) — 그래도 빈 화면 대신 앱의 첫 화면으로 보낸다.
  if (!version) {
    return <Navigate to="/" replace />;
  }
  const doc = version.docs[lang];
  const versions = policyVersions(kind, registry);
  const Root = standalone ? 'main' : 'div';

  const withParam = (key: string, value: string): string => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    return `?${next.toString()}`;
  };

  return (
    <Root className="flex min-h-dvh flex-col bg-background pb-8" lang={lang}>
      {standalone ? (
        <h1 className="break-keep px-[18px] pb-1 pt-8 text-[22px] font-semibold leading-[1.4] tracking-[-0.2px] text-bk-900">
          {doc.title}
        </h1>
      ) : (
        <PageHeader title={t(`web.policy.${kind}.title`)} />
      )}

      {/* 좁은 화면(320px)의 영어에서는 알약이 길어 언어 링크가 다음 줄로 내려간다. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 px-[18px] pt-3">
        {/* 시행일 알약 — 보이는 것은 글자 + 화살표, 투명한 셀렉트가 그 위를 덮어 어디를
            눌러도 OS 의 선택 메뉴가 열린다. 셀렉트는 알약보다 위아래로 4px 씩 더 받아 누르는
            자리가 44px 를 넘는다. */}
        <div className="relative flex items-center gap-1 rounded-full border border-bk-100 bg-white py-2 pl-3.5 pr-2.5 has-[select:focus-visible]:ring-2 has-[select:focus-visible]:ring-ring">
          <span className="whitespace-nowrap text-sm font-medium leading-[1.4] tracking-[-0.07px] text-bk-700">
            {formatEffective(version.effective, lang)}
          </span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-bk-700"
            aria-hidden="true"
          />
          <select
            aria-label={t('web.policy.version')}
            value={version.effective}
            onChange={(e) =>
              setParams(withParam('v', e.target.value), { replace: true })
            }
            className="absolute -inset-y-1 inset-x-0 cursor-pointer appearance-none opacity-0"
          >
            {versions.map(({ effective }) => (
              <option key={effective} value={effective}>
                {formatEffective(effective, lang)}
              </option>
            ))}
          </select>
        </div>

        {standalone && (
          <nav
            aria-label={t('web.policy.language')}
            className="flex shrink-0 items-center gap-1 text-sm tracking-[-0.07px]"
          >
            {POLICY_LANGUAGES.map((code) => (
              <Link
                key={code}
                to={withParam('lang', code)}
                replace
                lang={code}
                aria-current={code === lang ? 'true' : undefined}
                className={cn(
                  'flex min-h-11 items-center whitespace-nowrap px-2',
                  code === lang ? 'font-medium text-bk-900' : 'text-bk-600',
                )}
              >
                {/* 자국어 표기(한국어 · English) — 읽을 수 있는 쪽을 누른다. */}
                {t(`web.language.${code}`)}
              </Link>
            ))}
          </nav>
        )}
      </div>

      <div className="px-[18px] pt-4">
        <PolicyDocument doc={doc} summaryTitle={t('web.policy.summaryTitle')} />
      </div>
    </Root>
  );
};
